import React from 'react';
import { format } from 'date-fns';
import { IndianRupee, LightbulbIcon, Percent, ArrowDownNarrowWide, ArrowUpNarrowWide, Clock, Plane, BadgeIndianRupee } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FlightFilterNavProps {
  type: 'onward' | 'return';
  from: string;
  to: string;
  date: string;
  cheapestPrice?: number;
  fastestDuration?: string;
  maxCommission?: number;
  cardSize?: 'full' | 'half';
  onSortChange: (sortBy: string) => void;
}

const FlightFilterNav: React.FC<FlightFilterNavProps> = ({
  type,
  from,
  to,
  date,
  cheapestPrice,
  fastestDuration,
  maxCommission,
  cardSize,
  onSortChange,
}) => {
  const [activeSort, setActiveSort] = React.useState('price');
  const [sortOrder, setSortOrder] = React.useState<Record<string, 'asc' | 'desc'>>({
    duration: 'asc',
    departure: 'asc',
    arrival: 'asc',
    price: 'asc',
    commission: 'desc'
  });

  const handleCheapestFastest = (sortType: string) => {
    setSortOrder(prev => ({
      ...prev,
      [sortType]: 'asc'
    }));
    setActiveSort(sortType);
    onSortChange(`${sortType}_asc`);
  }

  const handleSort = (sortType: string) => {
    if (activeSort === sortType) {
      const newOrder = sortOrder[sortType] === 'asc' ? 'desc' : 'asc';
      setSortOrder(prev => ({
        ...prev,
        [sortType]: newOrder
      }));
      onSortChange(`${sortType}_${newOrder}`);
    } else {
      setActiveSort(sortType);
      onSortChange(`${sortType}_${sortOrder[sortType]}`);
    }
  };

  const SortButton = ({
    icon: Icon,
    label,
    value,
    sortType
  }: {
    icon: any,
    label: string,
    value: string | number | undefined,
    sortType: string
  }) => (
    <button
      onClick={() => handleCheapestFastest(sortType)}
      className={cn(
        "bg-gray-50 rounded-lg px-3 flex items-center space-x-2 transition-all cursor-pointer hover:bg-gray-100",
        activeSort === sortType && "ring-2 ring-primary"
      )}
    >
      <Icon className="text-primary" />
      <div>
        <div className="text-sm font-semibold">{label}</div>
        {value && (
          <div className="text-xs">
            {typeof value === 'number' ? `₹${value.toLocaleString()}` : value}
          </div>
        )}
      </div>
    </button>
  );

  let formattedDate = 'Invalid Date';
  try {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      formattedDate = format(parsedDate, 'EEE, dd MMM yyyy');
    }
  } catch (error) {
    console.error('Date parsing error:', error);
  }

  return (
    <div className="bg-white rounded-lg p-3 md:p-4 mb-4 ">
      <div className="flex flex-col space-y-3 md:space-y-4">
        {/* Route Info Section */}
        <div className={cn(
          "flex flex-col gap-2",
          cardSize === "half"
            ? "items-center text-center"
            : "md:flex-row md:justify-between"
        )}>
          {/* Quick Sort Options */}
          <div className={cn(
            "flex flex-wrap",
            cardSize === "half" ? "gap-2 justify-center" : "gap-2 md:gap-4"
          )}>
            <SortButton
              icon={BadgeIndianRupee}
              label="Cheapest"
              value={cheapestPrice}
              sortType="price"
            />
            <SortButton
              icon={Clock}
              label="Fastest"
              value={fastestDuration}
              sortType="duration"
            />

          </div>
        </div>

        {/* Advanced Sort Options */}
        <div className="border-t pt-3 md:pt-4">
          <div className="text-xs md:text-sm font-medium mb-2">Sort By:</div>
          <div className="flex flex-wrap gap-2 md:gap-3">
            {[
              { label: 'Duration', type: 'duration', icon: Clock },
              { label: 'Departure', type: 'departure', icon: Plane },
              { label: 'Arrival', type: 'arrival', icon: Plane },
              { label: 'Price', type: 'price', icon: BadgeIndianRupee }
            ].map(({ label, type, icon: Icon }) => (
              <button
                key={type}
                onClick={() => handleSort(type)}
                className={cn(
                  "flex items-center space-x-1 md:space-x-2 px-2 py-1.5 rounded border text-xs md:text-sm transition-all hover:bg-gray-50",
                  activeSort === type && "border-primary bg-red-50"
                )}
              >
                <Icon size={12} className="text-gray-500" />
                <span>{label}</span>
                {sortOrder[type] === 'asc' ? (
                  <ArrowDownNarrowWide size={12} className="ml-1" />
                ) : (
                  <ArrowUpNarrowWide size={12} className="ml-1" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightFilterNav;
